import { create } from 'zustand';
import type { SavedSearch, CarFilters } from '@/types';
import {
  getSavedSearches,
  createSavedSearch,
  updateSavedSearch,
  deleteSavedSearch,
  toggleEmailNotifications,
} from '@/lib/api';

interface SavedSearchState {
  savedSearches: SavedSearch[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSavedSearches: () => Promise<void>;
  addSavedSearch: (name: string, filters: CarFilters, sortBy: string, sortOrder: string) => Promise<boolean>;
  updateSavedSearchName: (id: string, name: string) => Promise<boolean>;
  toggleEmailNotifications: (id: string, enabled: boolean) => Promise<boolean>;
  removeSavedSearch: (id: string) => Promise<boolean>;
  hasSavedSearches: () => boolean;
  clearSavedSearches: () => void;
}

export const useSavedSearchStore = create<SavedSearchState>((set, get) => ({
  savedSearches: [],
  isLoading: false,
  error: null,

  fetchSavedSearches: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await getSavedSearches();
      const searches = response.data || [];
      set({
        savedSearches: searches,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch saved searches',
        isLoading: false,
      });
    }
  },

  addSavedSearch: async (name: string, filters: CarFilters, sortBy: string, sortOrder: string) => {
    try {
      const response = await createSavedSearch({
        name,
        filters,
        sortBy,
        sortOrder,
      });

      if (response.success && response.data) {
        set((state) => ({
          savedSearches: [response.data!, ...state.savedSearches],
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to save search:', error);
      return false;
    }
  },

  updateSavedSearchName: async (id: string, name: string) => {
    try {
      const response = await updateSavedSearch(id, name);

      if (response.success && response.data) {
        set((state) => ({
          savedSearches: state.savedSearches.map((s) =>
            s.id === id ? { ...s, name, updatedAt: response.data!.updatedAt } : s
          ),
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update saved search:', error);
      return false;
    }
  },

  toggleEmailNotifications: async (id: string, enabled: boolean) => {
    // Optimistic update
    const { savedSearches } = get();
    set((state) => ({
      savedSearches: state.savedSearches.map((s) =>
        s.id === id ? { ...s, emailNotifications: enabled } : s
      ),
    }));

    try {
      const response = await toggleEmailNotifications(id, enabled);

      if (response.success && response.data) {
        set((state) => ({
          savedSearches: state.savedSearches.map((s) =>
            s.id === id ? { ...s, emailNotifications: response.data!.emailNotifications, updatedAt: response.data!.updatedAt } : s
          ),
        }));
        return true;
      }
      // Revert on error
      set({ savedSearches });
      return false;
    } catch (error) {
      // Revert on error
      set({ savedSearches });
      console.error('Failed to toggle email notifications:', error);
      return false;
    }
  },

  removeSavedSearch: async (id: string) => {
    // Optimistic update
    const { savedSearches } = get();
    set((state) => ({
      savedSearches: state.savedSearches.filter((s) => s.id !== id),
    }));

    try {
      const response = await deleteSavedSearch(id);

      if (!response.success) {
        // Revert on error
        set({ savedSearches });
        return false;
      }
      return true;
    } catch (error) {
      // Revert on error
      set({ savedSearches });
      console.error('Failed to delete saved search:', error);
      return false;
    }
  },

  hasSavedSearches: () => {
    return get().savedSearches.length > 0;
  },

  clearSavedSearches: () => {
    set({ savedSearches: [], error: null });
  },
}));
